import { isPageEmbedded } from './is-page-embedded';

type EmbedWindow = Pick<
  Window,
  'parent' | 'requestAnimationFrame' | 'setInterval' | 'clearInterval' | 'setTimeout'
>;

type EmbedDocument = Pick<Document, 'body' | 'documentElement' | 'querySelectorAll'>;

type ResizeObserverConstructor = new (callback: ResizeObserverCallback) => ResizeObserver;

type EmbedHeightMessengerOptions = {
  document?: EmbedDocument;
  resizeObserver?: ResizeObserverConstructor;
  window?: EmbedWindow;
  /**
   * How long to keep polling after init for late-loading listing content
   * and images. Partner iframes (Impartner) only grow when we post a larger
   * height, so under-reporting leaves the product grid clipped.
   */
  settleMs?: number;
  pollIntervalMs?: number;
};

const DEFAULT_SETTLE_MS = 10_000;
const DEFAULT_POLL_INTERVAL_MS = 500;

export function measureEmbedDocumentHeight(targetDocument: EmbedDocument): number {
  const body = targetDocument.body;
  const html = targetDocument.documentElement;

  return Math.ceil(
    Math.max(
      body?.scrollHeight ?? 0,
      body?.offsetHeight ?? 0,
      html?.scrollHeight ?? 0,
      html?.offsetHeight ?? 0,
      html?.clientHeight ?? 0
    )
  );
}

/**
 * Eager-load lazy images so reserved card geometry finishes laying out while
 * the partner iframe is still settling its height. Without this, images below
 * the initial iframe viewport may never load and height stops growing.
 */
export function eagerLoadLazyImages(targetDocument: EmbedDocument): void {
  const images = targetDocument.querySelectorAll?.('img[loading="lazy"]');
  if (!images) return;

  images.forEach(node => {
    // Avoid HTMLImageElement instanceof so unit tests can run without a DOM.
    if (!node || typeof node !== 'object' || !('loading' in node)) return;
    (node as HTMLImageElement).loading = 'eager';
  });
}

export function initializeEmbedHeightMessenger({
  document: targetDocument = document,
  resizeObserver: ResizeObserverType = ResizeObserver,
  window: targetWindow = window,
  settleMs = DEFAULT_SETTLE_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS
}: EmbedHeightMessengerOptions = {}): void {
  if (!isPageEmbedded(targetDocument) || targetWindow.parent === targetWindow) {
    return;
  }

  let lastHeight = -1;
  let animationFramePending = false;

  const sendHeight = () => {
    animationFramePending = false;

    const height = measureEmbedDocumentHeight(targetDocument);
    if (height === lastHeight || height <= 0) return;

    lastHeight = height;
    // Impartner Showcase listens for a bare numeric height and grows the
    // nested iframe when the value increases (see _resizeIframe).
    targetWindow.parent.postMessage(height, '*');
  };

  const scheduleHeight = () => {
    if (animationFramePending) return;

    animationFramePending = true;
    targetWindow.requestAnimationFrame(sendHeight);
  };

  eagerLoadLazyImages(targetDocument);

  const resizeObserver = new ResizeObserverType(scheduleHeight);
  resizeObserver.observe(targetDocument.body);
  if (targetDocument.documentElement) {
    resizeObserver.observe(targetDocument.documentElement);
  }

  // Late product-search renders and image loads can miss the initial observer
  // pass; poll briefly so partner frames keep expanding to the full listing.
  const pollId = targetWindow.setInterval(() => {
    eagerLoadLazyImages(targetDocument);
    scheduleHeight();
  }, pollIntervalMs);

  targetWindow.setTimeout(() => {
    targetWindow.clearInterval(pollId);
  }, settleMs);

  scheduleHeight();
}
